import React, { useEffect } from "react";
import { toast } from "sonner";
import api from "../../lib/apiClient";
import { useAuth } from "../../context/AuthContext";
import {
  loadDraft, clearDraft, DRAFT_EXPIRY_MS,
  DEFAULT_LOCATION, selectDurationBracket
} from "../../lib/drafts";
import { cacheInvalidate } from "../../lib/cache";

// Background watcher: when a recording draft passes its 12-hour grace period,
// auto-publish it on the user's behalf and toast the resulting bill.
const DraftAutoPublisher = () => {
  const { user, refreshUser } = useAuth();
  const ranRef = React.useRef(false);
  useEffect(() => {
    if (!user || ranRef.current) return;
    ranRef.current = true;
    // Defer to idle so first paint isn't blocked. Falls back to a small timeout
    // on browsers without requestIdleCallback (Safari).
    const schedule = window.requestIdleCallback
      ? (cb) => window.requestIdleCallback(cb, { timeout: 5000 })
      : (cb) => setTimeout(cb, 1500);
    const cancel = window.cancelIdleCallback || clearTimeout;
    const handle = schedule(async () => {
      try {
        const draft = await loadDraft();
        if (!draft) return;
        const ageMs = Date.now() - (draft.startedAt || 0);
        if (ageMs < DRAFT_EXPIRY_MS) return;
        const ext = (draft.blob.type || '').includes('mp4') ? 'mp4' : 'webm';
        const fd = new FormData();
        fd.append('file', draft.blob, `recording.${ext}`);
        fd.append('title', `Auto-published recording from ${new Date(draft.startedAt).toLocaleString()}`);
        fd.append('location', JSON.stringify(draft.location || DEFAULT_LOCATION));
        fd.append('duration', String(selectDurationBracket(draft.recordedSeconds || 0)));
        fd.append('storage_days', '10');
        fd.append('featured', 'false');
        const res = await api.post(`/videos/upload`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        await clearDraft();
        cacheInvalidate('videos');
        cacheInvalidate('followingFeed');
        if (user?.username) cacheInvalidate(`profile:${user.username.toLowerCase()}`);
        toast.info(`Your draft recording aged out — auto-published. Charged ${res.data.total_cost.toFixed(2)} credits.`);
        if (refreshUser) refreshUser();
      } catch (err) {
        console.warn('DraftAutoPublisher failed', err);
      }
    });
    return () => { try { cancel(handle); } catch (e) { /* ignore */ } };
  }, [user, refreshUser]);
  return null;
};

export default DraftAutoPublisher;
